import React, { useEffect, useMemo, useRef, useState } from "react";
import { toI18N } from "../../i18n";
import { detectEmailProvider } from "../../utils/emailProvider";
import { PROVIDER_HINT_CONFIG } from "../../utils/emailProviderConfig";

type ProviderHintProps = {
	email: string;
};

function renderBody(text: string): React.ReactNode {
	const parts = text.split(/\*\*(.+?)\*\*/);
	return parts.map((part, i) =>
		i % 2 === 1 ? <strong key={i}>{part}</strong> : part,
	);
}

function EnvelopeIcon() {
	return (
		<svg
			xmlns="http://www.w3.org/2000/svg"
			viewBox="0 0 24 24"
			fill="none"
			stroke="currentColor"
			strokeWidth={2}
			strokeLinecap="round"
			strokeLinejoin="round"
			className="w-4 h-4 flex-shrink-0"
			aria-hidden="true"
		>
			<rect x="2" y="4" width="20" height="16" rx="2" />
			<path d="m22 7-8.97 5.7a1.94 1.94 0 0 1-2.06 0L2 7" />
		</svg>
	);
}

function ChevronIcon({ expanded }: { expanded: boolean }) {
	return (
		<svg
			xmlns="http://www.w3.org/2000/svg"
			viewBox="0 0 24 24"
			fill="none"
			stroke="currentColor"
			strokeWidth={2}
			strokeLinecap="round"
			strokeLinejoin="round"
			className={`w-4 h-4 flex-shrink-0 text-neutral-3 transition-transform duration-200 ${
				expanded ? "rotate-180" : ""
			}`}
			aria-hidden="true"
		>
			<path d="m6 9 6 6 6-6" />
		</svg>
	);
}

export default function ProviderHint({ email }: ProviderHintProps) {
	const [expanded, setExpanded] = useState(false);
	const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

	const category = useMemo(() => detectEmailProvider(email), [email]);
	const config = PROVIDER_HINT_CONFIG[category];

	useEffect(() => {
		timerRef.current = setTimeout(() => {
			setExpanded(true);
		}, 30_000);
		return () => {
			if (timerRef.current !== null) clearTimeout(timerRef.current);
		};
	}, []);

	if (!email) return null;

	return (
		<div className="mt-4 rounded-lg border border-neutral-7 overflow-hidden">
			<button
				type="button"
				onClick={() => setExpanded((prev) => !prev)}
				aria-expanded={expanded}
				className="w-full flex items-center gap-2 px-4 py-3 bg-primary-4 text-left cursor-pointer text-primary-3"
			>
				<EnvelopeIcon />
				<span className="text-sm flex-1">{toI18N(config.collapsedLabel)}</span>
				<ChevronIcon expanded={expanded} />
			</button>
			<div
				role="status"
				aria-live="polite"
				className={`overflow-hidden transition-all duration-300 ${
					expanded ? "max-h-48" : "max-h-0"
				}`}
			>
				<div className="px-4 pb-4 pt-2 bg-primary-4 text-sm text-primary-3 border-t border-neutral-7">
					<p className="font-semibold mb-1">{toI18N(config.title)}</p>
					<p>{renderBody(toI18N(config.body))}</p>
				</div>
			</div>
		</div>
	);
}
